import React, { useEffect, useState, useCallback } from "react";

import {
  Box,
  Typography,
  Divider,
  Button,
  IconButton,
  CircularProgress
} from "@mui/material";
import PotentialCandidateForm from "../PotentialCandidateForm";
import { updateMatchStatus } from "../../ctapi/idealCandidates";
import { useOrganization } from "../../shared/OrganizationContext";
import { Close } from "@mui/icons-material";

const scoreToColor = (label) => {
  return label === "skills" ? "#1a73e8" : "#34a853";
};

const fadedColor = (label) => {
  return label === "skills" ? "#e3f2fd" : "#e6f4ea";
};

const buildCSemiCircle = (score, label, animate) => {
  const circleCount = 9;
  const filledCount = Math.round(score * circleCount);
  const angles = [60, 75, 95, 120, 150, 185, 222, 265, 315];
  const scalePattern = [0.7, 1.10, 1.45, 1.75, 1.95, 2.15, 2.35, 2.95, 3.25];
  const radius = 80;
  const baseSize = 20;

  return (
    <Box sx={{ position: "relative", width: 240, height: 240, margin: 'auto' }}>
      {angles.map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const x = 120 + radius * Math.cos(rad);
        const y = 120 + radius * Math.sin(rad);
        const size = baseSize * scalePattern[i];
        return (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: y,
              left: x,
              width: size,
              height: size,
              borderRadius: "50%",
              backgroundColor: i < (animate ? filledCount : 0) ? scoreToColor(label) : fadedColor(label),
              transform: "translate(-50%, -50%)",
              transition: "background-color 0.6s ease",
              transitionDelay: `${i * 0.1}s`
            }}
          />
        );
      })}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center"
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: scoreToColor(label) }}>
          {Math.round(score * 100)}%
        </Typography>
        <Typography variant="body2" sx={{color: 'text.secondary'}}>
          {label === "skills" ? "Skill Score" : "Behavior Score"}
        </Typography>
      </Box>
    </Box>
  );
};

const PotentialCandidateView = ({
  idealCandidate,
  potentialCandidate,
  onClose
}) => {
  const { organization } = useOrganization();
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (potentialCandidate) {
      const timeout = setTimeout(() => setAnimate(true), 300);
      return () => clearTimeout(timeout);
    }
  }, [potentialCandidate]);

  const onDeclineHandler = useCallback(async () => {
    if (!organization || !idealCandidate?.id || !potentialCandidate?.id) {
        console.error("Missing data for decline operation:", {
            hasOrg: !!organization,
            hasIdealId: !!idealCandidate?.id,
            hasPotentialId: !!potentialCandidate?.id
        });
        return;
    }
    try {
        await updateMatchStatus(organization, idealCandidate.id, potentialCandidate.id, "declined");
        window.location.href = `/ideal-candidates/${idealCandidate.id}`;
    } catch (error) {
        console.error("Error declining candidate:", error);
    }
  }, [organization, idealCandidate, potentialCandidate]);

  const onApproveHandler = useCallback(async () => {
    if (!organization || !idealCandidate?.id || !potentialCandidate?.id) {
        console.error("Missing data for approve operation:", {
            hasOrg: !!organization,
            hasIdealId: !!idealCandidate?.id,
            hasPotentialId: !!potentialCandidate?.id
        });
        return;
    }
    try {
        await updateMatchStatus(organization, idealCandidate.id, potentialCandidate.id, "approved");
        window.location.href = `/ideal-candidates/${idealCandidate.id}`;
    } catch (error) {
        console.error("Error approving candidate:", error);
    }
  }, [organization, idealCandidate, potentialCandidate]);

  if (!potentialCandidate || !potentialCandidate.scorecardResults) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ml: 2, color: 'text.secondary'}}>Loading candidate data...</Typography>
      </Box>
    );
  }

  const scoreMap = potentialCandidate.scorecardResults.reduce((acc, res) => {
    acc[res.scorecardName] = res.score;
    return acc;
  }, {});

  return (
    <Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.5,
          boxShadow: 1,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fff"
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Candidate Review
        </Typography>
        <IconButton onClick={onClose} aria-label="close">
          <Close />
        </IconButton>
      </Box>


      <Box
        sx={{
          display: "flex",
          justifyContent: "center",

          alignItems: 'center',
          flexWrap: 'wrap',
          gap: { xs: 3, md: 6 },
          py: { xs: 3, md: 5 },

          backgroundColor: "#fafafa",
          borderBottom: "1px solid #eee"
        }}
      >

        {typeof scoreMap["skills"] === 'number' && buildCSemiCircle(scoreMap["skills"], "skills", animate)}
        {typeof scoreMap["behaviors"] === 'number' && buildCSemiCircle(scoreMap["behaviors"], "behaviors", animate)}
      </Box>

      <Box sx={{ maxWidth: "900px", margin: "0 auto", py: 4, px: 2 }}>

        <Typography variant="h6" sx={{ mb: 2 }}>
          Candidate's Answers
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <PotentialCandidateForm
          idealCandidate={idealCandidate}
          potentialCandidate={potentialCandidate}
          includeHeader={false}
          viewOnly={true}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button variant="outlined" color="error" onClick={onDeclineHandler}>
                Decline
            </Button>
            <Button variant="contained" color="primary" onClick={onApproveHandler}>
                Approve
            </Button>
        </Box>

      </Box>
    </Box>
  );
};

export default PotentialCandidateView;
